import React, { useState } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import {
  TRS_BANDS,
  TEI_BANDS,
  PCS_BANDS,
  LAI_BANDS,
  CRI_BANDS,
  LII_BANDS,
} from '../../utils/reportingConstants';

/**
 * Terminology Page - Explains all metrics used in Aktivity
 * Shows descriptions and interpretation bands for ARS, TRS, TEI, PCS, LAI, CRI, LII
 */

const TerminologyPage = () => {
  const [activeSection, setActiveSection] = useState('overview');

  const sections = [
    { id: 'overview', name: 'Overview' },
    { id: 'ars', name: 'Action Readiness Score (ARS)' },
    { id: 'feedback-metrics', name: 'Feedback-Based Metrics' },
    { id: 'engagement', name: 'Engagement Score' },
    { id: 'session-metrics', name: 'Session-Level Metrics' },
  ];

  // Metric card component - shows description and interpretation bands only
  const MetricCard = ({ title, shortName, description, bands, color }) => (
    <div className="bg-white border border-neutral-200 rounded-xl p-4 sm:p-6 hover:shadow-md transition-shadow">
      <div className="flex items-start gap-3 sm:gap-4 mb-3 sm:mb-4">
        <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center text-white font-bold text-sm sm:text-lg flex-shrink-0 ${color}`}>
          {shortName}
        </div>
        <div className="flex-1">
          <h3 className="text-sm sm:text-lg font-semibold text-neutral-900">{title}</h3>
          <p className="text-xs sm:text-sm text-neutral-600">{description}</p>
        </div>
      </div>

      {/* Bands */}
      {bands && (
        <div>
          <p className="text-[10px] sm:text-xs text-neutral-500 uppercase tracking-wide mb-2">Interpretation Bands</p>
          <div className="space-y-1.5 sm:space-y-2">
            {Object.entries(bands).map(([key, band]) => (
              <div key={key} className="flex items-center gap-2 text-xs sm:text-sm">
                <span className={`w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full flex-shrink-0 ${
                  band.color === 'green' ? 'bg-green-500' :
                  band.color === 'yellow' ? 'bg-yellow-500' :
                  band.color === 'orange' ? 'bg-orange-500' : 'bg-red-500'
                }`}></span>
                <span className="text-neutral-600">{band.min} - {band.max}:</span>
                <span className="font-medium text-neutral-800">{band.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <DashboardLayout>
      <div className="p-4 lg:p-6 pb-20 lg:pb-6">
        <div className="max-w-6xl mx-auto">
            {/* Page Header */}
            <div className="mb-6 sm:mb-8">
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-neutral-900 mb-1 sm:mb-2">
                Terminology
              </h1>
              <p className="text-xs sm:text-sm text-neutral-600">
                Understanding Aktivity metrics and how training effectiveness scores are calculated.
              </p>
            </div>

            {/* Section Navigation */}
            <div className="flex flex-wrap gap-1.5 sm:gap-2 mb-6 sm:mb-8">
              {sections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors ${
                    activeSection === section.id
                      ? 'bg-primary-500 text-white'
                      : 'bg-white border border-neutral-200 text-neutral-700 hover:bg-neutral-50'
                  }`}
                >
                  {section.name}
                </button>
              ))}
            </div>

            {/* Overview Section */}
            {activeSection === 'overview' && (
              <div className="space-y-6">
                <div className="bg-white border border-neutral-200 rounded-xl p-4 sm:p-6">
                  <h2 className="text-base sm:text-xl font-semibold text-neutral-900 mb-3 sm:mb-4">
                    Aktivity Metrics Overview
                  </h2>
                  <p className="text-xs sm:text-base text-neutral-600 mb-4 sm:mb-6">
                    Aktivity uses a comprehensive set of metrics to measure training effectiveness,
                    participant engagement, and learning outcomes. These metrics are calculated from
                    participant responses during and after training sessions.
                  </p>

                  <div className="grid md:grid-cols-2 gap-3 sm:gap-4">
                    <div className="p-3 sm:p-4 bg-blue-50 rounded-lg border border-blue-100">
                      <h3 className="text-sm sm:text-base font-semibold text-blue-900 mb-1.5 sm:mb-2">Action-Based Metrics</h3>
                      <ul className="text-xs sm:text-sm text-blue-800 space-y-1">
                        <li>ARS - Action Readiness Score</li>
                      </ul>
                      <p className="text-[10px] sm:text-xs text-blue-600 mt-1.5 sm:mt-2">
                        Calculated from action plans with timeline and commitment levels
                      </p>
                    </div>

                    <div className="p-3 sm:p-4 bg-purple-50 rounded-lg border border-purple-100">
                      <h3 className="text-sm sm:text-base font-semibold text-purple-900 mb-1.5 sm:mb-2">Feedback-Based Metrics</h3>
                      <ul className="text-xs sm:text-sm text-purple-800 space-y-1">
                        <li>TRS - Topic Relevance Score</li>
                        <li>TEI - Trainer Effectiveness Index</li>
                        <li>PCS - Participant Confidence Signal</li>
                        <li>LAI - Learning Advocacy Index</li>
                        <li>CRI - Challenge Resolution Index</li>
                        <li>LII - Learning Implementation Indicator</li>
                      </ul>
                      <p className="text-[10px] sm:text-xs text-purple-600 mt-1.5 sm:mt-2">
                        Calculated from 5-point scale feedback questions
                      </p>
                    </div>
                  </div>
                </div>

                {/* Quick Reference Cards
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="bg-white border border-neutral-200 rounded-xl p-5">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mb-3">
                      <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                      </svg>
                    </div>
                    <h3 className="font-semibold text-neutral-900 mb-1">7 Core Metrics</h3>
                    <p className="text-sm text-neutral-600">Comprehensive coverage of learning effectiveness</p>
                  </div>

                  <div className="bg-white border border-neutral-200 rounded-xl p-5">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mb-3">
                      <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                    </div>
                    <h3 className="font-semibold text-neutral-900 mb-1">3-5 Bands per Metric</h3>
                    <p className="text-sm text-neutral-600">Clear interpretation guidelines</p>
                  </div>

                  <div className="bg-white border border-neutral-200 rounded-xl p-5">
                    <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mb-3">
                      <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    </div>
                    <h3 className="font-semibold text-neutral-900 mb-1">Multi-Level Views</h3>
                    <p className="text-sm text-neutral-600">Participant, Trainer, and Client perspectives</p>
                  </div>
                </div> */}
              </div>
            )}

            {/* ARS Section */}
            {activeSection === 'ars' && (
              <div className="space-y-6">
                <div className="bg-white border border-neutral-200 rounded-xl p-4 sm:p-6">
                  <div className="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-6">
                    <div className="w-10 h-10 sm:w-14 sm:h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center text-white font-bold text-sm sm:text-xl flex-shrink-0">
                      ARS
                    </div>
                    <div>
                      <h2 className="text-base sm:text-xl font-semibold text-neutral-900">Action Readiness Score</h2>
                      <p className="text-xs sm:text-base text-neutral-600">Measures likelihood of participants taking action based on their timeline and commitment level</p>
                    </div>
                  </div>

                  {/* Description */}
                  <div className="mb-4 sm:mb-6">
                    <p className="text-xs sm:text-base text-neutral-600">
                      The Action Readiness Score evaluates how prepared participants are to implement what they've learned.
                      It considers two key factors: the urgency of their planned timeline (when they intend to take action)
                      and their commitment level (how strongly they feel about following through).
                    </p>
                  </div>

                  {/* ARS Bands */}
                  <div>
                    <h3 className="text-sm sm:text-base font-semibold text-neutral-900 mb-3 sm:mb-4">Interpretation Bands</h3>
                    <div className="grid md:grid-cols-3 gap-3 sm:gap-4">
                      <div className="p-3 sm:p-4 bg-green-50 rounded-lg border border-green-200">
                        <div className="flex items-center gap-2 mb-1.5 sm:mb-2">
                          <span className="w-2.5 h-2.5 sm:w-3 sm:h-3 bg-green-500 rounded-full"></span>
                          <span className="text-xs sm:text-base font-semibold text-green-800">High Readiness</span>
                        </div>
                        <p className="text-xs sm:text-sm text-green-700 mb-1.5 sm:mb-2">Score: 0.70 - 1.00</p>
                        <p className="text-[10px] sm:text-xs text-green-600">
                          Participant has clear timeline and strong commitment to take action.
                        </p>
                      </div>

                      <div className="p-3 sm:p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                        <div className="flex items-center gap-2 mb-1.5 sm:mb-2">
                          <span className="w-2.5 h-2.5 sm:w-3 sm:h-3 bg-yellow-500 rounded-full"></span>
                          <span className="text-xs sm:text-base font-semibold text-yellow-800">Moderate Readiness</span>
                        </div>
                        <p className="text-xs sm:text-sm text-yellow-700 mb-1.5 sm:mb-2">Score: 0.40 - 0.69</p>
                        <p className="text-[10px] sm:text-xs text-yellow-600">
                          Participant shows intent but may need support or clearer action steps.
                        </p>
                      </div>

                      <div className="p-3 sm:p-4 bg-red-50 rounded-lg border border-red-200">
                        <div className="flex items-center gap-2 mb-1.5 sm:mb-2">
                          <span className="w-2.5 h-2.5 sm:w-3 sm:h-3 bg-red-500 rounded-full"></span>
                          <span className="text-xs sm:text-base font-semibold text-red-800">Low Readiness</span>
                        </div>
                        <p className="text-xs sm:text-sm text-red-700 mb-1.5 sm:mb-2">Score: 0.00 - 0.39</p>
                        <p className="text-[10px] sm:text-xs text-red-600">
                          Participant is uncertain about taking action. May need additional guidance.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Feedback-Based Metrics Section */}
            {activeSection === 'feedback-metrics' && (
              <div className="space-y-6">
                {/* Introduction */}
                <div className="bg-white border border-neutral-200 rounded-xl p-4 sm:p-6">
                  <h2 className="text-base sm:text-xl font-semibold text-neutral-900 mb-3 sm:mb-4">Feedback-Based Metrics</h2>
                  <p className="text-xs sm:text-base text-neutral-600">
                    These metrics are derived from participant feedback collected at the end of training sessions.
                    Each metric captures a different aspect of the training effectiveness and participant experience.
                  </p>
                </div>

                {/* Metric Cards Grid */}
                <div className="grid lg:grid-cols-2 gap-6">
                  {/* TRS */}
                  <MetricCard
                    title="Topic Relevance Score"
                    shortName="TRS"
                    description="Measures how relevant participants found the training content to their work and daily responsibilities."
                    bands={TRS_BANDS}
                    color="bg-gradient-to-br from-teal-500 to-teal-600"
                  />

                  {/* TEI */}
                  <MetricCard
                    title="Trainer Effectiveness Index"
                    shortName="TEI"
                    description="Evaluates how effective participants found the trainer in explaining concepts and delivering content clearly."
                    bands={TEI_BANDS}
                    color="bg-gradient-to-br from-purple-500 to-purple-600"
                  />

                  {/* PCS */}
                  <MetricCard
                    title="Participant Confidence Signal"
                    shortName="PCS"
                    description="Indicates how confident participants feel about applying what they learned in their actual work."
                    bands={PCS_BANDS}
                    color="bg-gradient-to-br from-amber-500 to-amber-600"
                  />

                  {/* LAI */}
                  <MetricCard
                    title="Learning Advocacy Index"
                    shortName="LAI"
                    description="Reflects how likely participants are to recommend the training to their colleagues and peers."
                    bands={LAI_BANDS}
                    color="bg-gradient-to-br from-pink-500 to-pink-600"
                  />

                  {/* CRI */}
                  <MetricCard
                    title="Challenge Resolution Index"
                    shortName="CRI"
                    description="Shows how well the training addressed the challenges and pain points participants face in their roles."
                    bands={CRI_BANDS}
                    color="bg-gradient-to-br from-orange-500 to-orange-600"
                  />

                  {/* LII */}
                  <MetricCard
                    title="Learning Implementation Indicator"
                    shortName="LII"
                    description="Measures individual participant's likelihood of implementing what they learned in their daily work."
                    bands={LII_BANDS}
                    color="bg-gradient-to-br from-indigo-500 to-indigo-600"
                  />
                </div>
              </div>
            )}

            {/* Engagement Score Section */}
            {activeSection === 'engagement' && (
              <div className="space-y-6">
                <div className="bg-white border border-neutral-200 rounded-xl p-4 sm:p-6">
                  <h2 className="text-base sm:text-xl font-semibold text-neutral-900 mb-3 sm:mb-4">Engagement Metrics</h2>
                  <p className="text-xs sm:text-base text-neutral-600 mb-4 sm:mb-6">
                    Engagement metrics measure how actively participants interact with session activities and content.
                  </p>

                  {/* Session Engagement */}
                  <div className="mb-6 sm:mb-8">
                    <h3 className="text-sm sm:text-base font-semibold text-neutral-900 mb-3 sm:mb-4">Session Engagement Rate</h3>
                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-4 sm:p-6 border border-green-200">
                      <p className="text-xs sm:text-sm text-neutral-600">
                        Shows the percentage of participants who actively engaged with the session by submitting
                        at least one response to any activity. Higher engagement rates indicate more interactive
                        and participatory sessions.
                      </p>
                    </div>
                  </div>

                  {/* Activity-Level Engagement */}
                  <div className="mb-6 sm:mb-8">
                    <h3 className="text-sm sm:text-base font-semibold text-neutral-900 mb-3 sm:mb-4">Activity Participation Rate</h3>
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 sm:p-6 border border-blue-200">
                      <p className="text-xs sm:text-sm text-neutral-600">
                        Measures participation for each individual activity within a session. This helps identify
                        which activities resonated most with participants and which may need improvement.
                      </p>
                    </div>
                  </div>

                  {/* Participation Metrics */}
                  <div>
                    <h3 className="text-sm sm:text-base font-semibold text-neutral-900 mb-3 sm:mb-4">Individual Participation Metrics</h3>
                    <div className="grid md:grid-cols-2 gap-3 sm:gap-4">
                      <div className="p-3 sm:p-4 bg-neutral-50 rounded-lg border border-neutral-200">
                        <h4 className="text-xs sm:text-base font-medium text-neutral-800 mb-1.5 sm:mb-2">Activities Completed</h4>
                        <p className="text-[10px] sm:text-sm text-neutral-600">
                          Number of unique activities where the participant submitted a response during the session.
                        </p>
                      </div>

                      <div className="p-3 sm:p-4 bg-neutral-50 rounded-lg border border-neutral-200">
                        <h4 className="text-xs sm:text-base font-medium text-neutral-800 mb-1.5 sm:mb-2">Participation Rate</h4>
                        <p className="text-[10px] sm:text-sm text-neutral-600">
                          Percentage of total session activities the participant engaged with and completed.
                        </p>
                      </div>

                      <div className="p-3 sm:p-4 bg-neutral-50 rounded-lg border border-neutral-200">
                        <h4 className="text-xs sm:text-base font-medium text-neutral-800 mb-1.5 sm:mb-2">Challenges Submitted</h4>
                        <p className="text-[10px] sm:text-sm text-neutral-600">
                          Total number of challenges submitted by the participant across all topics/subsessions.
                        </p>
                      </div>

                      <div className="p-3 sm:p-4 bg-neutral-50 rounded-lg border border-neutral-200">
                        <h4 className="text-xs sm:text-base font-medium text-neutral-800 mb-1.5 sm:mb-2">Action Plans Created</h4>
                        <p className="text-[10px] sm:text-sm text-neutral-600">
                          Total number of action plans created by the participant across all topics/subsessions.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Session-Level Metrics Section */}
            {activeSection === 'session-metrics' && (
              <div className="space-y-6">
                <div className="bg-white border border-neutral-200 rounded-xl p-4 sm:p-6">
                  <h2 className="text-base sm:text-xl font-semibold text-neutral-900 mb-3 sm:mb-4">Session-Level Metrics</h2>
                  <p className="text-xs sm:text-base text-neutral-600 mb-4 sm:mb-6">
                    Session-level metrics aggregate individual participant data to provide overall session insights
                    and help organizations understand training effectiveness at a glance.
                  </p>

                  {/* Per-Topic/Subsession Metrics */}
                  <div className="mb-6 sm:mb-8">
                    <h3 className="text-sm sm:text-base font-semibold text-neutral-900 mb-3 sm:mb-4">Per-Topic (Subsession) Metrics</h3>
                    <p className="text-xs sm:text-base text-neutral-600 mb-3 sm:mb-4">
                      When a session has multiple topics or subsessions, metrics are tracked separately for each
                      topic to help identify which areas performed well and which need improvement.
                    </p>
                    <div className="grid md:grid-cols-2 gap-3 sm:gap-4">
                      <div className="p-3 sm:p-4 bg-neutral-50 rounded-lg border border-neutral-200">
                        <h4 className="text-xs sm:text-base font-medium text-neutral-800 mb-1.5 sm:mb-2">Score Metrics</h4>
                        <p className="text-[10px] sm:text-sm text-neutral-600">
                          ARS, TRS, TEI, PCS, CRI, LII are averaged per topic and also aggregated across all topics
                          to give an overall session score.
                        </p>
                      </div>
                      <div className="p-3 sm:p-4 bg-neutral-50 rounded-lg border border-neutral-200">
                        <h4 className="text-xs sm:text-base font-medium text-neutral-800 mb-1.5 sm:mb-2">Count Metrics</h4>
                        <p className="text-[10px] sm:text-sm text-neutral-600">
                          Challenges, action plans, and responses are counted per topic and summed across all topics
                          for the session total.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Trainer Aggregations */}
                  <div className="p-3 sm:p-5 bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl border border-amber-200">
                    <h3 className="text-sm sm:text-base font-semibold text-amber-900 mb-2 sm:mb-3">Trainer Performance Overview</h3>
                    <p className="text-xs sm:text-sm text-amber-800 mb-3 sm:mb-4">
                      Trainer metrics provide an overview of trainer performance across all their conducted sessions:
                    </p>
                    <div className="grid md:grid-cols-3 gap-3 sm:gap-4">
                      <div className="bg-white rounded-lg p-2.5 sm:p-3 border border-amber-200">
                        <p className="text-xs sm:text-base font-medium text-neutral-800">TEI Average</p>
                        <p className="text-[10px] sm:text-xs text-neutral-600">Overall trainer effectiveness rating across sessions</p>
                      </div>
                      <div className="bg-white rounded-lg p-2.5 sm:p-3 border border-amber-200">
                        <p className="text-xs sm:text-base font-medium text-neutral-800">LAI Average</p>
                        <p className="text-[10px] sm:text-xs text-neutral-600">How likely participants recommend the trainer's sessions</p>
                      </div>
                      <div className="bg-white rounded-lg p-2.5 sm:p-3 border border-amber-200">
                        <p className="text-xs sm:text-base font-medium text-neutral-800">Total Sessions</p>
                        <p className="text-[10px] sm:text-xs text-neutral-600">Number of completed training sessions</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Challenge Density */}
                <div className="bg-white border border-neutral-200 rounded-xl p-4 sm:p-6">
                  <h3 className="text-sm sm:text-base font-semibold text-neutral-900 mb-3 sm:mb-4">Challenge Density Analysis</h3>
                  <p className="text-xs sm:text-base text-neutral-600">
                    Identifies which topics generated the most challenges from participants, helping organizations
                    and trainers identify areas that need additional attention or support.
                  </p>
                </div>

                {/* Top Challenges Analysis */}
                <div className="bg-white border border-neutral-200 rounded-xl p-4 sm:p-6">
                  <h3 className="text-sm sm:text-base font-semibold text-neutral-900 mb-3 sm:mb-4">Top Challenges Analysis</h3>
                  <p className="text-xs sm:text-base text-neutral-600">
                    Analyzes challenge submissions to find common themes and frequently mentioned topics.
                    This helps identify recurring pain points across participants that may need to be addressed
                    in future training sessions or organizational improvements.
                  </p>
                </div>
              </div>
            )}

            {/* Footer Note */}
            <div className="mt-6 sm:mt-8 p-3 sm:p-4 bg-neutral-100 rounded-xl border border-neutral-200">
              <div className="flex items-start gap-2 sm:gap-3">
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-neutral-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <p className="text-xs sm:text-sm text-neutral-600">
                    These metrics are designed to provide actionable insights for participants, trainers, and organizations
                    to measure and improve training effectiveness.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </DashboardLayout>
  );
};

export default TerminologyPage;
